import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getApiV1ExamExamidLangsQueryKey, usePutApiV1ExamLangsId } from '@/shared/api/generated';

/**
 * Hook para alternar o status (ativo/rascunho) de um idioma de prova via
 * PUT /api/v1/exam/langs/{id}, enviando apenas o campo `active`.
 *
 * Invalida a query de idiomas da prova após sucesso.
 *
 * @param examId - ID da prova à qual o idioma pertence
 * @returns Handler de toggle, ID em andamento e estado de loading
 */
export function useToggleExamLangStatus(examId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examManagement');
  const [pendingId, setPendingId] = useState<number | null>(null);

  const mutation = usePutApiV1ExamLangsId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamExamidLangsQueryKey(examId),
        });
        setPendingId(null);
      },
      onError: () => {
        setPendingId(null);
        alert({ alertType: 'warning', title: t('toast.error') });
      },
    },
  });

  const toggleStatus = (id: number, cultureInfo: string, active: boolean) => {
    setPendingId(id);
    mutation.mutate({ id, data: { cultureInfo, active } });
  };

  return { toggleStatus, pendingId, isPending: mutation.isPending } as const;
}
